import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Stack, Tooltip, Typography } from "@mui/material";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import { useThemeMode } from "../../../theme/ThemeModeProvider";
import { chatRailTokens } from "../tokens";
import { ToolCallCard } from "./ToolCallCard";
import { renderTextWithCitations, type MemoryCitation } from "./MemoryCitationChip";
import { supabase } from "../../../lib/supabase";

export interface MessageRow {
  id: number;
  conversation_id: string;
  role: string;
  content: unknown;
  iteration: number | null;
  run_id: string | null;
  created_at: string | null;
  model_name: string | null;
  model_provider: string | null;
  stop_reason: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
}

interface ContentBlock {
  type: string;
  text?: string;
  name?: string;
  id?: string;
  input?: unknown;
  content?: unknown;
  tool_use_id?: string;
}

function asBlocks(content: unknown): ContentBlock[] {
  if (typeof content === "string") return [{ type: "text", text: content }];
  if (Array.isArray(content)) return content as ContentBlock[];
  if (content && typeof content === "object") {
    const obj = content as ContentBlock & { content?: unknown };
    if (obj.type) return [obj];
    // Defensive: rows persisted before 20260503000001 stored the full
    // {role, content} envelope in agent_messages.content. Unwrap so those
    // legacy threads still render.
    if ("content" in obj && obj.content !== undefined) return asBlocks(obj.content);
  }
  return [];
}

function plainText(blocks: ContentBlock[]): string {
  return blocks
    .filter((b) => b.type === "text" && typeof b.text === "string")
    .map((b) => b.text!)
    .join("\n\n");
}

export function MessageBubble({
  message,
  onOpenRun,
  agentId,
  citations = [],
}: {
  message: MessageRow;
  onOpenRun: (runId: string) => void;
  agentId?: string;
  citations?: MemoryCitation[];
}) {
  const { mode } = useThemeMode();
  const T = chatRailTokens(mode);
  const navigate = useNavigate();
  const [branching, setBranching] = useState(false);

  async function handleBranch() {
    if (!message.conversation_id) return;
    setBranching(true);
    try {
      const { data, error } = await supabase.rpc("branch_conversation", {
        p_conversation_id: message.conversation_id,
        p_message_id: message.id,
      });
      if (error) throw error;
      navigate(`/conversations/${data as string}`);
    } catch {
      // silently ignore — user can retry
    } finally {
      setBranching(false);
    }
  }
  const blocks = asBlocks(message.content);
  const role = message.role;
  const text = plainText(blocks);
  const toolUses = blocks.filter((b) => b.type === "tool_use");
  const toolResults = blocks.filter((b) => b.type === "tool_result");

  const isUser = role === "user";
  const isAssistant = role === "assistant";
  const isTool = role === "tool";

  const align = isUser ? "flex-end" : "flex-start";
  // Per HANDOFF foundation rule: accent is for chrome only. User
  // bubbles use the neutral panel2 + hair2 surface (parts-thread.jsx:
  // "user surface — panel2 + 1px hair2").
  const bg = isUser ? T.panel2 : isTool ? T.panel : T.rail;
  const border = isUser ? T.hair2 : T.hair;

  return (
    <Box sx={{ display: "flex", justifyContent: align }}>
      <Box
        sx={{
          maxWidth: "85%",
          minWidth: 0,
          bgcolor: bg,
          border: `1px solid ${border}`,
          borderRadius: `${T.r_md}px`,
          px: 1.5,
          py: 1,
          fontFamily: T.sans,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" mb={0.25}>
          <Typography
            variant="caption"
            sx={{
              color: T.textDim,
              fontSize: T.fs_micro,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            {role}
          </Typography>
          {message.model_name && isAssistant && (
            <Typography variant="caption" sx={{ color: T.textMute, fontSize: T.fs_micro }}>
              {message.model_provider}/{message.model_name}
            </Typography>
          )}
          {message.run_id && (
            <Box
              component="button"
              type="button"
              onClick={() => onOpenRun(message.run_id!)}
              sx={{
                border: "none",
                bgcolor: "transparent",
                cursor: "pointer",
                color: T.accent,
                fontSize: T.fs_micro,
                p: 0,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              run
            </Box>
          )}
          {message.created_at && (
            <Typography variant="caption" sx={{ color: T.textMute, fontSize: T.fs_micro }}>
              {new Date(message.created_at).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </Typography>
          )}
          <Tooltip title="Branch conversation from here">
            <Box
              component="button"
              type="button"
              onClick={handleBranch}
              disabled={branching}
              sx={{
                border: "none",
                bgcolor: "transparent",
                cursor: "pointer",
                color: T.textMute,
                p: 0,
                display: "flex",
                alignItems: "center",
                "&:hover": { color: T.accent },
                "&:disabled": { cursor: "not-allowed", opacity: 0.5 },
              }}
            >
              {branching ? <CircularProgress size={10} /> : <CallSplitIcon sx={{ fontSize: 12 }} />}
            </Box>
          </Tooltip>
        </Stack>
        {text && (
          <Typography
            component="div"
            sx={{
              fontSize: T.fs_body,
              color: T.text,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: T.sans,
            }}
          >
            {citations.length > 0 && agentId
              ? renderTextWithCitations(text, citations, agentId)
              : text}
          </Typography>
        )}
        {toolUses.map((tu) => (
          <Box key={tu.id ?? `${message.id}-${tu.name}`} sx={{ mt: 1 }}>
            <ToolCallCard
              kind="invocation"
              skillName={tu.name ?? "unknown"}
              input={tu.input}
              onOpenRun={message.run_id ? () => onOpenRun(message.run_id!) : undefined}
            />
          </Box>
        ))}
        {toolResults.map((tr) => (
          <Box key={tr.tool_use_id ?? `${message.id}-result`} sx={{ mt: 1 }}>
            <ToolCallCard kind="result" skillName="result" input={tr.content} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}
